import React from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';

import NvIcon from 'shared/components/nv-icon';
import ClickableContainer from 'components/clickable-container';
import { gray5, primary } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import NvTooltip from 'shared/components/nv-tooltip';
import { useFormContext } from 'react-hook-form';
import { config } from '../../../../../../config/pendo.config.json';

const styles = css`
  .add-more {
    width: max-content;
  }
  .objective-textarea-container {
    min-height: 45px;
    border-radius: ${quarterSpacing}px;
    box-shadow: 0 0 0 1px ${gray5};
    width: 100%;
    .trash-container {
      visibility: hidden;
    }
    &:focus-within {
      box-shadow: 0 0 0 1px ${primary};
    }
    &:hover, &:focus-within {
      .trash-container {
        visibility: visible;
      }
    }
    .objective-textarea {
      border-radius: ${quarterSpacing}px;
      min-height: 100%;
      width: 90%;
      .nv-text-area {
        height: 100%;
        display: flex;
        align-items: center;
        textarea {
          border: none;
          box-shadow: none;
          min-height: 100%;
          background-color: transparent;
        }
        .bs4-input-group-text {
          align-items: flex-start;
          background: transparent;
          border: none;
          padding-right: ${quarterSpacing}px;
        }
      }
    }
  }
`;
const ObjectiveInputList = (props) => {
  const {
    name,
    fields,
    handleAddField,
    handleRemoveField,
    isAddingField,
    maxFieldCount,
  } = props;

  const handleAddAIObjective = () => {
    handleAddField();
  };

  const handleRemoveObjective = (id) => {
    handleRemoveField(id);
  };

  const getAddText = () => {
    if (name === 'aiCharacterObjections' && fields.length === 0) {
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.ADD_OBJECTIONS();
    }
    return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.ADD_MORE();
  };

  const getDeleteDataQa = () => {
    switch (name) {
      case 'aiCharacterObjections':
        return config.pendo.activities.aiRolePlay.configModal.deleteObjection;
      case 'learnerGoals':
      default:
        return config.pendo.activities.aiRolePlay.configModal.deleteLearnerGoal;
    }
  };

  const getAddDataQa = () => {
    switch (name) {
      case 'aiCharacterObjections':
        return config.pendo.activities.aiRolePlay.configModal.addObjection;
      case 'learnerGoals':
      default:
        return config.pendo.activities.aiRolePlay.configModal.addLearnerGoal;
    }
  };

  const isAddMoreDisabled = fields.length >= maxFieldCount || isAddingField;

  const { register } = useFormContext();

  return (
    <div css={styles}>
      {fields && fields.map((objective: any, index) => (
        <div className='d-flex objective-textarea-container align-items-center mt-2' key={objective.fieldId}>
          {/* Hidden input to register the id field with the form */}
          <input type='hidden' {...register(`${name}[${index}].id`)} />
          <NvTextArea
            name={`${name}[${index}].text`}
            withForm
            key={objective.fieldId}
            className='objective-textarea bg-white align-items-center'
            textareaClassName='text-large-body lh-25'
          />
          <ClickableContainer
            className='ml-auto pr-3 trash-container'
            aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.DELETE_FIELD()}
            onClick={() => {
              // Invariant: objective.id is always present and unique.
              // In create mode: UUID string, in edit mode: numeric DB id.
              handleRemoveObjective(objective.id);
            }}
            data-qa={getDeleteDataQa()}
          >
            <NvIcon
              icon='trash'
              size='small'
              className='text-primary'
            />
          </ClickableContainer>
        </div>
      ))}
      <ClickableContainer
        className='add-more'
        disabled={isAddMoreDisabled}
        onClick={handleAddAIObjective}
        data-qa={getAddDataQa()}
      >
        <NvTooltip
          text={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.DISABLED_ADD_MORE_TOOLTIP(5)}
          enabled={isAddMoreDisabled}
        >
          <div className={`d-flex mt-2 ${isAddMoreDisabled ? 'text-gray-4' : 'text-primary'}`}>
            <NvIcon size='small' icon='add' />
            <p
              className='mb-0 ml-2'
            >
              {getAddText()}
            </p>
          </div>
        </NvTooltip>
      </ClickableContainer>
    </div>
  );
};

export default ObjectiveInputList;
